import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { useTranslation } from 'next-i18next'

import { PAGE_TYPES } from '@src/constants/page-type'
import { isMobileDevice } from '@src/quince-site-common/src/helpers/getDeviceTypeForPageRouter'
import SomethingWentWrongPage from '@src/quince-site-common/src/organisms/appLayout/molecules/errorBoundary/somethingWentWrong/ErrorPage'
import { isProd } from '@src/quince-site-common/src/utils/isEnv'
import { ServerSideBaseProps } from '@src/server-side/api/api.types'
import { getServerSideBaseProps } from '@src/server-side/get-server-side-props'

import Layout from '../layout'

import CollectionPageTemplate from './page-templates/collection-page-template'
import FaqPageTemplate from './page-templates/faq-page-template'
import LandingPageTemplate from './page-templates/landing-page-template'
import PartnerPDPPageTemplate from './page-templates/partner-pdp-page-template'
import ProductAndBundlePageTemplate from './page-templates/product-and-bundle-page-template'

const PAGE_TEMPLATES = {
  [PAGE_TYPES.COLLECTION]: CollectionPageTemplate,
  [PAGE_TYPES.SUBCOLLECTION]: CollectionPageTemplate,
  [PAGE_TYPES.PARTNER_PDP]: PartnerPDPPageTemplate,
  [PAGE_TYPES.FAQ]: FaqPageTemplate,
  [PAGE_TYPES.PRODUCT_AND_BUNDLE]: ProductAndBundlePageTemplate,
  [PAGE_TYPES.LANDING]: LandingPageTemplate,
}

const SERVER_SIDE_RENDER_TIME_HEADER = 'X-Server-Side-Render-Time'

type PageTemplateType = keyof typeof PAGE_TEMPLATES

function isValidPageType(pageType: string | undefined): pageType is PageTemplateType {
  return Object.keys(PAGE_TEMPLATES).includes(pageType as PageTemplateType)
}

type ServerSidePRops =
  | ({ type: '_page' } & ServerSideBaseProps)
  | {
      type: '_ignore_path'
    }

// @i18n-ignore
const CATCH_ALL_IGNORE_PREFIXES = ['_next', 'static-assets', 'api', 'ecomm-ssr']

export const getServerSideProps = (async ctx => {
  const pathName = Array.isArray(ctx.params?.slug) ? ctx.params.slug.join('/') : ''
  if (CATCH_ALL_IGNORE_PREFIXES.some(prefix => pathName.startsWith(prefix))) {
    return {
      props: {
        // @i18n-ignore
        type: '_ignore_path',
      },
    }
  }
  const { slug: _, ...query } = ctx.query
  //add server time to all SSR requests
  const start = performance.now()
  const serverSideBaseProps = await getServerSideBaseProps(
    ctx,
    pathName,
    new URLSearchParams(query as Record<string, string>),
  )
  const end = performance.now()
  const duration = end - start
  ctx.res.setHeader(SERVER_SIDE_RENDER_TIME_HEADER, `${duration}ms`)
  if ('props' in serverSideBaseProps) {
    const props = await serverSideBaseProps.props
    return {
      props: {
        // @i18n-ignore
        type: '_page',
        ...props,
      },
    }
  }

  return serverSideBaseProps
}) satisfies GetServerSideProps<ServerSidePRops>

export default function CatchAllPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { t } = useTranslation('common')
  if (props.type != '_page') {
    return null
  }
  try {
    if (props.pageData) {
      const ssrPageType = props.pageData.context?.ssrPageType
      if (isValidPageType(ssrPageType)) {
        const PageTemplate = PAGE_TEMPLATES[ssrPageType]
        return (
          <Layout {...props} overrideChildrenForNon200={true}>
            <PageTemplate
              pathname={props.pathName}
              pageData={props.pageData}
              isMobile={isMobileDevice(props.deviceType)}
              serverSideFeatureFlags={props.serverSideFeatureFlags}
            />
          </Layout>
        )
      }
    } else if (props.siteData) {
      return (
        <Layout {...props}>
          <SomethingWentWrongPage showHeader={false} />
        </Layout>
      )
    }

    return (
      <SomethingWentWrongPage showHeader>
        {!isProd ? null : (
          <div>
            {/* eslint-disable-next-line react/jsx-no-literals */}
            <h1>{t('should_render_404_page')}</h1>
            <pre
              style={{
                // @i18n-ignore
                overflow: 'scroll',
              }}
            >
              {JSON.stringify(props, null, 2)}
            </pre>
          </div>
        )}
      </SomethingWentWrongPage>
    )
  } catch (err) {
    return (
      <SomethingWentWrongPage showHeader>
        {!isProd ? null : (
          <div>
            {/* eslint-disable-next-line react/jsx-no-literals */}
            <h1>{t('should_render_400_page')}</h1>
            <pre>{JSON.stringify(err, null, 2)}</pre>
            <pre>{JSON.stringify(props, null, 2)}</pre>
          </div>
        )}
      </SomethingWentWrongPage>
    )
  }
}
